import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Badge,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  DescriptionOutlined as DocumentIcon,
  Visibility as PreviewIcon,
  Upload as UploadIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import {
  Button as RButton,
  Card as RCard,
  CardHeader as RCardHeader,
  Collapse as RCollapse,
} from 'reactstrap';
import FeatherIcon from 'feather-icons-react';
import Swal from 'sweetalert2';
import { DocumentPreviewUI } from '../DocumentPreview';
import { DOCUMENT_TYPES } from './Constants';
import axios from '../../../../../api/axios';

const getSignedUrl = async ({ key }) => {
  try {
    const response = await axios.post('/v2/master_sheet/archive/signed-url', { key });
    return response.data?.data?.signedUrl || '';
  } catch (error) {
    console.error('Error fetching signed URL:', error);
    throw new Error('Failed to fetch signed URL');
  }
};

const inferFileName = (url, fallback) => {
  const base = (fallback || '').trim() || 'document';
  try {
    const pathname = new URL(url).pathname;
    const last = decodeURIComponent(pathname.split('/').pop() || '');
    const extMatch = last.match(/\.[A-Za-z0-9]{1,8}$/);
    if (extMatch && !/\.[A-Za-z0-9]{1,8}$/.test(base)) {
      return `${base}${extMatch[0]}`;
    }
  } catch {
    // ignore
  }
  return base;
};

const triggerBrowserDownload = async (url, fileName) => {
  if (!url || typeof window === 'undefined') return;
  const finalName = inferFileName(url, fileName);

  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Fetch failed: ${response.status}`);
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);

    const link = window.document.createElement('a');
    link.href = blobUrl;
    link.download = finalName;
    window.document.body.appendChild(link);
    link.click();
    window.document.body.removeChild(link);
    setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
  } catch (err) {
    console.error('Blob download failed, falling back to direct link:', err);
    const link = window.document.createElement('a');
    link.href = url;
    link.download = finalName;
    link.rel = 'noopener noreferrer';
    window.document.body.appendChild(link);
    link.click();
    window.document.body.removeChild(link);
  }
};

const StaticDocumentCard = ({ documentType, documents, onPreview }) => {
  const hasDocuments = documents && documents.length > 0;
  const documentCount = documents[0]?.previous_versions?.length ? documents[0].previous_versions.length + 1 : hasDocuments ? 1 : 0;
  const isVisible = documentType.alwaysVisible || hasDocuments;

  if (!isVisible) return null;

  return (
    <Grid item xs={12} sm={6} md={6} key={documentType.id}>
      <Card
        variant="outlined"
        sx={{
          border: 2,
          borderColor: hasDocuments ? 'success.main' : 'error.main',
          backgroundColor: hasDocuments ? 'rgb(225 255 227)' : 'rgb(255 233 233)',
          '&:hover': {
            boxShadow: 3,
            transform: 'translateY(-2px)',
          },
          transition: 'all 0.3s ease',
          minWidth: 150,
        }}
      >
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, overflow: 'hidden' }}>
              {hasDocuments ? (
                <CheckIcon sx={{ color: 'success.main', mr: 1.5, fontSize: 20 }} />
              ) : (
                <ErrorIcon sx={{ color: 'error.main', mr: 1.5, fontSize: 20 }} />
              )}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="subtitle2"
                  fontWeight="bold"
                  noWrap
                  title={documentType.title}
                >
                  {documentType.title}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                  {documentType.description}{' '}
                  {documentType.autoDelete && (
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{ fontWeight: 'bold', fontSize: '0.65rem' }}
                    >
                      ⚠️ Auto-deletes in {documentType.deleteDays} days
                    </Typography>
                  )}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5, mt: 1 }}>
              <Tooltip title={`Preview ${documentType.title} Documents`}>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => onPreview(documentType, documents)}
                  sx={{
                    border: 1,
                    borderColor: 'primary.main',
                    p: 0.5,
                    '&:hover': { backgroundColor: 'primary.light' },
                  }}
                >
                  <Badge
                    badgeContent={documentCount}
                    color={hasDocuments ? 'success' : 'error'}
                    sx={{
                      '& .MuiBadge-badge': {
                        fontSize: '0.6rem',
                        minWidth: 16,
                        height: 16,
                        padding: '0 4px',
                      },
                    }}
                  >
                    <PreviewIcon fontSize="small" sx={{ color: hasDocuments ? 'primary.main' : 'error.main' }} />
                  </Badge>
                </IconButton>
              </Tooltip>
              <Tooltip title={`Upload ${documentType.title}`}>
                <IconButton
                  size="small"
                  color="secondary"
                  onClick={() => onPreview(documentType, documents)}
                  sx={{
                    border: 1,
                    borderColor: 'secondary.main',
                    p: 0.5,
                    '&:hover': { backgroundColor: 'secondary.light' },
                  }}
                >
                  <UploadIcon fontSize="small" sx={{ color: hasDocuments ? 'secondary.main' : 'error.main' }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Grid>
  );
};

const DocumentTypePreviewDialog = ({ open, onClose, documentType, documents }) => {
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showLatest, setShowLatest] = useState(true);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [signedUrls, setSignedUrls] = useState(new Map());

  // Fetch signed URLs for all documents when dialog opens
  useEffect(() => {
    const fetchSignedUrls = async () => {
      if (!open || !documents || documents.length === 0) return;

      const urlMap = new Map();

      try {
        if (documents[0] && documents[0].key) {
          const signedUrl = await getSignedUrl({ key: documents[0].key });
          if (signedUrl) urlMap.set(documents[0].key, signedUrl);
        }

        if (documents[0]?.previous_versions) {
          await Promise.all(
            documents[0].previous_versions.map(async (version) => {
              if (version.key) {
                try {
                  const signedUrl = await getSignedUrl({ key: version.key });
                  if (signedUrl) urlMap.set(version.key, signedUrl);
                } catch (error) {
                  console.error(`Error fetching signed URL for version ${version.version_number}:`, error);
                }
              }
            })
          );
        }

        setSignedUrls(urlMap);
      } catch (error) {
        console.error('Error fetching signed URLs:', error);
        setPreviewError('Failed to load document URLs. Please try again.');
      }
    };

    if (open) {
      fetchSignedUrls();
    } else {
      // Reset state when dialog closes
      setSignedUrls(new Map());
      setSelectedDocument(null);
      setShowLatest(true);
      setPreviewError(null);
    }
  }, [open, documents]);

  // Set initial selected document after signed URLs are fetched
  useEffect(() => {
    if (open && documents && documents.length > 0 && signedUrls.size > 0) {
      const latestDoc = documents[0];
      if (latestDoc && latestDoc.key && signedUrls.has(latestDoc.key)) {
        setSelectedDocument({
          ...latestDoc,
          signedUrl: signedUrls.get(latestDoc.key)
        });
        setShowLatest(true);
        setPreviewError(null);
      }
    }
  }, [open, documents, signedUrls]);

  const handleDocumentMetroSelect = async (document, isLatest = false) => {
    setLoadingPreview(true);
    setPreviewError(null);

    try {
      if (!document.key) {
        throw new Error('Document key not available');
      }

      let signedUrl = signedUrls.get(document.key);

      if (!signedUrl) {
        signedUrl = await getSignedUrl({ key: document.key });
        if (signedUrl) {
          setSignedUrls(prev => new Map(prev).set(document.key, signedUrl));
        }
      }

      setSelectedDocument({
        ...document,
        signedUrl,
      });
      setShowLatest(isLatest);
    } catch (error) {
      console.error('Error loading document preview:', error);
      setPreviewError('Failed to load document preview. Please try again.');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleDownload = async (document) => {
    if (typeof window === 'undefined') return;

    try {
      let downloadUrl;

      if (document.key) {
        downloadUrl = signedUrls.get(document.key);
        if (!downloadUrl) {
          try {
            downloadUrl = await getSignedUrl({ key: document.key });
            if (downloadUrl) {
              setSignedUrls(prev => new Map(prev).set(document.key, downloadUrl));
            }
          } catch (error) {
            console.error('Error getting signed URL for download:', error);
            downloadUrl = document.url;
          }
        }
      } else {
        downloadUrl = document.url;
      }

      if (!downloadUrl || downloadUrl === 'upload_requested') {
        throw new Error('Document not available for download');
      }

      const fileName =
        document.name ||
        document.document_type ||
        (documentType?.title ? `${documentType.title}_${document.version_number || 'latest'}` : '');

      await triggerBrowserDownload(downloadUrl, fileName);
    } catch (error) {
      console.error('Error opening document:', error);
      Swal.fire({
        title: 'Error',
        text: `Failed to open document: ${error.message}`,
        icon: 'error',
        confirmButtonText: 'OK',
      });
    }
  };

  const handleDownloadAll = async () => {
    try {
      if (!documents || documents.length === 0) {
        Swal.fire('No Documents', 'No documents available to open.', 'info');
        return;
      }

      // Show loading indicator
      Swal.fire({
        title: 'Opening Documents...',
        text: 'Please wait while we prepare your documents.',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      let openCount = 0;
      let failedCount = 0;
      const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

      // Open latest documents
      for (const doc of documents) {
        if (getDocumentAvailability(doc)) {
          try {
            await handleDownload(doc);
            openCount++;
            await delay(1000); // Delay to avoid popup blocker
          } catch (error) {
            console.error(`Failed to open document:`, error);
            failedCount++;
          }
        }

        // Open previous versions
        if (doc.previous_versions && Array.isArray(doc.previous_versions)) {
          for (const version of doc.previous_versions) {
            if (getDocumentAvailability(version)) {
              try {
                await handleDownload(version);
                openCount++;
                await delay(1000); // Delay to avoid popup blocker
              } catch (error) {
                console.error(`Failed to open version ${version.version_number}:`, error);
                failedCount++;
              }
            }
          }
        }
      }

      // Close loading and show result
      Swal.close();

      if (openCount === 0 && failedCount === 0) {
        Swal.fire('No Documents', 'No valid documents were available to open.', 'info');
      } else if (openCount > 0 && failedCount === 0) {
        Swal.fire('Success', `${openCount} document${openCount > 1 ? 's' : ''} opened successfully.`, 'success');
      } else if (openCount > 0 && failedCount > 0) {
        Swal.fire('Partial Success', `${openCount} document${openCount > 1 ? 's' : ''} opened successfully. ${failedCount} failed.`, 'warning');
      } else {
        Swal.fire('Error', 'All documents failed to open. Please check your network connection and try again.', 'error');
      }
    } catch (error) {
      Swal.close();
      console.error('Error in open all process:', error);
      Swal.fire('Error', 'An unexpected error occurred. Please try again.', 'error');
    }
  };

 
  // Utility function to check document availability
  const getDocumentAvailability = (document) => {
    if (!document) return false;
    return (document.key && document.key !== '') ||
      (document.url && document.url !== '' && document.url !== 'upload_requested');
  };

  const handleToggleLatest = () => {
    if (documents && documents.length > 0) {
      if (!showLatest) {
        handleDocumentMetroSelect(documents[0], true);
      } else {
        setShowLatest(false);
      }
    }
  };


  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6">
            {documentType?.title || 'Documents'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {documents && documents.length > 0 && (
            <>
              <Button
                variant="outlined"
                color="primary"
                size="small"
                startIcon={<FeatherIcon icon="download" size="16" />}
                onClick={handleDownloadAll}
              >
                Download All
              </Button>
              <Button
                variant="contained"
                color="secondary"
                size="small"
                startIcon={<FeatherIcon icon="upload" size="16" />}
                onClick={onClose}
              >
                Upload New
              </Button>
            </>
          )}
          <IconButton onClick={onClose} size="small">
            <FeatherIcon icon="x" size="16" />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {documents && documents.length > 0 ? (
          <Box sx={{ height: '70vh', display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ flex: '1 1 auto', overflow: 'auto' }}>
              {loadingPreview ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
                  <CircularProgress />
                  <Typography variant="body2" ml={2}>Loading preview...</Typography>
                </Box>
              ) : previewError ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
                  <Alert severity="error">{previewError}</Alert>
                </Box>
              ) : selectedDocument && selectedDocument?.url && selectedDocument?.signedUrl ? (
                <DocumentPreviewUI
                  documentUrl={selectedDocument?.url || ''}
                  previewURL={selectedDocument?.signedUrl || ''}
                  width="100%"
                  height="60vh"
                />
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
                  <Typography color="text.secondary">Document preview not available.</Typography>
                </Box>
              )}
            </Box>
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Document Versions
                </Typography>
                <FormControlLabel
                  control={<Switch checked={showLatest} onChange={handleToggleLatest} size="small" />}
                  label="Show Latest"
                  labelPlacement="start"
                  sx={{ m: 0 }}
                />
              </Box>
              <Grid container spacing={1}>
                {documents[0] && (
                  <Grid item xs={6} sm={6} md={4}>
                    <Card
                      variant="outlined"
                      sx={{
                        border: 2,
                        borderColor: getDocumentAvailability(documents[0]) ? 'success.main' : 'error.main',
                        backgroundColor: selectedDocument === documents[0] && showLatest
                          ? 'primary.light'
                          : getDocumentAvailability(documents[0])
                            ? 'rgb(225 255 227)'
                            : 'rgb(255 233 233)',
                        '&:hover': {
                          boxShadow: 3,
                          transform: 'translateY(-2px)',
                        },
                        transition: 'all 0.3s ease',
                        minWidth: 120,
                        cursor: 'pointer',
                      }}
                      onClick={() => handleDocumentMetroSelect(documents[0], true)}
                    >
                      <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, overflow: 'hidden' }}>
                            {getDocumentAvailability(documents[0]) ? (
                              <CheckIcon sx={{ color: 'success.main', mr: 1, fontSize: 18 }} />
                            ) : (
                              <DocumentIcon sx={{ color: 'error.main', mr: 1, fontSize: 18 }} />
                            )}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography
                                variant="caption"
                                fontWeight="bold"
                                noWrap
                                title="Latest Version"
                              >
                                Latest
                              </Typography>
                              <br />
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                                Comments: {documents[0].comments || 'N/A'}
                              </Typography>
                              <br />
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                                Uploaded: {documents[0].updated_at ? new Date(documents[0].updated_at).toLocaleDateString() : 'N/A'}
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Tooltip title="Preview Document">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDocumentMetroSelect(documents[0], true);
                                }}
                                disabled={!getDocumentAvailability(documents[0])}
                                sx={{
                                  border: 1,
                                  borderColor: 'primary.main',
                                  p: 0.4,
                                  '&:hover': { backgroundColor: 'primary.light' },
                                }}
                              >
                                <PreviewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Download Document">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownload(documents[0]);
                                }}
                                disabled={!getDocumentAvailability(documents[0])}
                                sx={{
                                  border: 1,
                                  borderColor: 'primary.main',
                                  p: 0.4,
                                  '&:hover': { backgroundColor: 'primary.light' },
                                }}
                              >
                                <FeatherIcon icon="download" size="12" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
                {documents[0]?.previous_versions?.map((version) => (
                  <Grid item xs={6} sm={6} md={4} key={version.version_number}>
                    <Card
                      variant="outlined"
                      sx={{
                        border: 2,
                        borderColor: getDocumentAvailability(version) ? 'success.main' : 'error.main',
                        backgroundColor: selectedDocument?.version_number === version.version_number && !showLatest
                          ? 'primary.light'
                          : getDocumentAvailability(version)
                            ? 'rgb(225 255 227)'
                            : 'rgb(255 233 233)',
                        '&:hover': {
                          boxShadow: 3,
                          transform: 'translateY(-2px)',
                        },
                        transition: 'all 0.3s ease',
                        minWidth: 120,
                        cursor: 'pointer',
                      }}
                      onClick={() => handleDocumentMetroSelect(version, false)}
                    >
                      <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, overflow: 'hidden' }}>
                            {getDocumentAvailability(version) ? (
                              <CheckIcon sx={{ color: 'success.main', mr: 1, fontSize: 18 }} />
                            ) : (
                              <DocumentIcon sx={{ color: 'error.main', mr: 1, fontSize: 18 }} />
                            )}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Tooltip title={version.comments || 'No comment available'}>
                                <Typography
                                  variant="caption"
                                  fontWeight="bold"
                                  noWrap
                                  title={`Version ${version.version_number}`}
                                >
                                  V{version.version_number}
                                </Typography>
                              </Tooltip>
                              <br />
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                                Comments: {version.comments || 'N/A'}
                              </Typography>
                              <br />
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                                Uploaded: {version.updated_at ? new Date(version.updated_at).toLocaleDateString() : 'N/A'}
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Tooltip title="Preview Document">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDocumentMetroSelect(version, false);
                                }}
                                disabled={!getDocumentAvailability(version)}
                                sx={{
                                  border: 1,
                                  borderColor: 'primary.main',
                                  p: 0.4,
                                  '&:hover': { backgroundColor: 'primary.light' },
                                }}
                              >
                                <PreviewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Download Document">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownload(version);
                                }}
                                disabled={!getDocumentAvailability(version)}
                                sx={{
                                  border: 1,
                                  borderColor: 'primary.main',
                                  p: 0.4,
                                  '&:hover': { backgroundColor: 'primary.light' },
                                }}
                              >
                                <FeatherIcon icon="download" size="12" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4, height: '70vh' }}>
            <DocumentIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No {documentType?.title} documents found
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              Upload documents to get started
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<FeatherIcon icon="upload" size="16" />}
              onClick={onClose}
            >
              Upload First Document
            </Button>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

const Documents = ({ documents }) => {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedDocumentType, setSelectedDocumentType] = useState(null);
  const [selectedDocuments, setSelectedDocuments] = useState([]);

  const getDocumentsByType = (typeId) => {
    const documentType = DOCUMENT_TYPES.find(dt => dt.id === typeId)?.document_type;
    const filteredDocs = documents.filter(doc =>
      doc.document_type === documentType ||
      (doc.name && documentType && doc.name.toLowerCase().trim().includes(documentType.toLowerCase().trim()))
    );
    return filteredDocs;
  };

  const handlePreviewOpen = (documentType, documentsOfType) => {
    setSelectedDocumentType(documentType);
    setSelectedDocuments(documentsOfType || []);
    setPreviewOpen(true);
  };

  const handlePreviewClose = () => {
    setPreviewOpen(false);
    setSelectedDocumentType(null);
    setSelectedDocuments([]);
  };

  return (
    <Box>
      <Grid container spacing={2} sx={{ mt: 0, pt: 0 }}>
        {DOCUMENT_TYPES.map((documentType) => {
          const documentsOfType = getDocumentsByType(documentType.id);
          const hasDocuments = documentsOfType && documentsOfType.length > 0;
          if (documentType.alwaysVisible || hasDocuments) {
            return (
              <StaticDocumentCard
                key={documentType.id}
                documentType={documentType}
                documents={documentsOfType}
                onPreview={handlePreviewOpen}
              />
            );
          }
          return null;
        })}
      </Grid>
      <DocumentTypePreviewDialog
        open={previewOpen}
        onClose={handlePreviewClose}
        documentType={selectedDocumentType}
        documents={selectedDocuments}
      />
    </Box>
  );
};

const DocumentsWrapper = ({ documents }) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleDownloadAll = async () => {
    Swal.fire({
      title: 'Are you sure?',
      text: 'Do you want to download all documents?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, download',
      cancelButtonText: 'No, cancel',
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          if (documents.length === 0) {
            Swal.fire('No Documents', 'No documents available for download.', 'info');
            return;
          }

          const resolveUrl = async (item) => {
            if (item.key) {
              try {
                const url = await getSignedUrl({ key: item.key });
                if (url) return url;
              } catch (error) {
                console.error('Error getting signed URL:', error);
              }
            }
            return item.url;
          };

          let downloadCount = 0;
          for (const doc of documents) {
            try {
              const downloadUrl = await resolveUrl(doc);
              if (downloadUrl && downloadUrl !== 'upload_requested') {
                await triggerBrowserDownload(
                  downloadUrl,
                  doc.name || doc.document_type || `document_${doc.created_at}`
                );
                downloadCount++;
                await new Promise((resolve) => setTimeout(resolve, 400));
              }
            } catch (err) {
              console.error(`Error downloading document ${doc.document_type}:`, err);
            }

            if (doc.previous_versions) {
              for (const version of doc.previous_versions) {
                try {
                  const downloadUrl = await resolveUrl(version);
                  if (downloadUrl && downloadUrl !== 'upload_requested') {
                    await triggerBrowserDownload(
                      downloadUrl,
                      version.name || version.document_type || `document_${version.version_number}`
                    );
                    downloadCount++;
                    await new Promise((resolve) => setTimeout(resolve, 400));
                  }
                } catch (err) {
                  console.error(`Error downloading version ${version.version_number}:`, err);
                }
              }
            }
          }

          if (downloadCount === 0) {
            Swal.fire('No Documents', 'No valid documents were available for download.', 'info');
          } else {
            Swal.fire('Success', `${downloadCount} document${downloadCount > 1 ? 's' : ''} downloaded successfully.`, 'success');
          }
        } catch (err) {
          console.error('Error downloading documents:', err);
          Swal.fire('Error', 'Failed to download documents.', 'error');
        }
      }
    });
  };

  return (
    <RCard className="mb-4">
      <RCardHeader className="py-3 text-center">
        <RButton
          color="primary"
          outline="true"
          className="fw-bold px-4 py-2 rounded-pill"
          onClick={() => setIsOpen((prev) => !prev)}
        >
          <FeatherIcon icon="file-text" className="me-2" size="16" />
          Documents ({DOCUMENT_TYPES.filter(dt => dt.alwaysVisible || documents.some(doc =>
            doc.document_type === dt.document_type ||
            (doc.name && dt.document_type && doc.name.toLowerCase().trim().includes(dt.document_type.toLowerCase().trim()))
          )).length})
        </RButton>
      </RCardHeader>
      <RCollapse isOpen={isOpen}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '15px', gap: 2 }}>
            <RButton
              color="secondary"
              className="d-flex align-items-center gap-1"
              style={{ fontSize: '0.875rem', padding: '4px 12px', borderRadius: '20px' }}
              onClick={() => handleDownloadAll()}
            >
              <FeatherIcon icon="download" size="16" />
              Download All
            </RButton>
          </Box>
          <Documents documents={documents} />
        </CardContent>
      </RCollapse>
    </RCard>
  );
};

DocumentsWrapper.propTypes = {
  documents: PropTypes.arrayOf(
    PropTypes.shape({
      url: PropTypes.string,
      key: PropTypes.string,
      document_type: PropTypes.string,
      name: PropTypes.string,
      comments: PropTypes.string,
      size: PropTypes.number,
      previous_versions: PropTypes.array,
      created_by: PropTypes.string,
      created_at: PropTypes.string,
      updated_at: PropTypes.string,
    })
  ).isRequired,
};

export { DocumentsWrapper as Documents };